import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  ERROR_TOURNAMENT_UNREGISTER,
  GET_TOURNAMENT,
  GOT_TOURNAMENT_UNREGISTER
} from 'constants/eventConstants';
import { DualSocketContext as SocketContext } from 'contexts/DualSocketContext';
import useGameService from 'services/GameService';
import { decompressData } from 'utils/functions';
import {
  isSitGoUnregisterInFlight,
  markSitGoUnregisterSent,
  settleSitGoUnregister,
  sitGoUnregisterReleaseIn
} from 'utils/sitGoUnregisterInFlight';

/**
 * How long the panel waits for gotTnmUnRegister before saying it is still
 * confirming and offering Leave table. The button stays locked after it: the
 * request may still be queued in the transport, and a second one could be
 * replayed beside it.
 */
export const SIT_GO_UNREGISTER_TIMEOUT_MS = 10_000;

const SUCCESS_TITLE = 'Unregister Successful';
export const UNREGISTER_FAILED_TITLE = 'Unregistration Failed';
export const STILL_CONFIRMING_MESSAGE = 'Still confirming your unregistration…';
export const CONNECTION_LOST_MESSAGE =
  'Connection lost. You can unregister again once you are back online.';
// No answer at all within SIT_GO_UNREGISTER_RELEASE_MS: the lock is released.
export const UNREGISTER_UNCONFIRMED_TITLE = 'Unregister not confirmed';
export const UNREGISTER_UNCONFIRMED_MESSAGE =
  'We heard nothing back. Check Tournament details before you try again.';

// Only the success title counts as unregistered. The engine's "You have
// already unregistered" reply is a failure here: it also sends it when its
// registration lookup fails (a database error reads as no row), so the player
// may still be registered. Shown as a failure, it offers Leave table.

export type SitGoUnregisterFailure = { title: string; message: string };

type Options = {
  tournamentId: number;
  gameID: number | string | undefined;
  /** Runs once the server confirms; the caller leaves the table. */
  onUnregistered: () => void;
};

/**
 * EN-11257: the waiting panel's Unregister. Emits the same request as the
 * pre-start modal's Unregister (GameService.unregister), but leaves the table
 * only once the server confirms it: gotTnmUnRegister carries both outcomes,
 * told apart by `response.title`, and error_gotTnmUnRegister is a failure.
 * App.tsx already does the success cleanup (registration snapshot, table
 * slice, playerGames); this hook adds the busy state, the failure message,
 * and the move off the table.
 *
 * At most one request per tournament is ever in flight from this panel,
 * across remounts (utils/sitGoUnregisterInFlight). The engine's unregister is not
 * idempotent (two can refund twice), and socket.io buffers an emit made while
 * disconnected and replays it on reconnect, so:
 *  - nothing is sent while the event's socket cannot deliver; the button
 *    stays usable for when the connection is back;
 *  - once sent, the button is locked until this tournament's reply arrives.
 *    Silence past SIT_GO_UNREGISTER_TIMEOUT_MS changes the message, and the
 *    panel offers Leave table, which sends no unregister, so the player is
 *    never stuck on the table. Silence past SIT_GO_UNREGISTER_RELEASE_MS
 *    releases the lock with an "unconfirmed" notice (see the util for why
 *    that is safe), so a lost reply cannot block Unregister for the session.
 */
export const useSitGoUnregister = ({ tournamentId, gameID, onUnregistered }: Options) => {
  const { socket } = useContext(SocketContext);
  const { unregister } = useGameService();
  // A panel reopened over an unanswered request starts locked, already past
  // the wait: how long the request has been out is not known here.
  const [pending, setPending] = useState(() => isSitGoUnregisterInFlight(tournamentId));
  const [stillConfirming, setStillConfirming] = useState(() =>
    isSitGoUnregisterInFlight(tournamentId)
  );
  const [connectionLost, setConnectionLost] = useState(false);
  const [failure, setFailure] = useState<SitGoUnregisterFailure | null>(null);
  // This panel is waiting on a reply. Its own flag, not the shared marker:
  // App.tsx's listener may settle the marker before this one runs.
  const awaitingRef = useRef(isSitGoUnregisterInFlight(tournamentId));
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onUnregisteredRef = useRef(onUnregistered);
  onUnregisteredRef.current = onUnregistered;

  const clearTimer = () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => {
    if (!socket) return undefined;
    const handleResult = (raw: any) => {
      const data = decompressData(raw);
      if (!data || !awaitingRef.current) return;
      if (data.tournamentId !== undefined && Number(data.tournamentId) !== tournamentId) return;
      awaitingRef.current = false;
      settleSitGoUnregister(tournamentId);
      clearTimer();
      setPending(false);
      setStillConfirming(false);
      const title = data?.response?.title || '';
      const message = data?.response?.msg || data?.msg || '';
      if (title === SUCCESS_TITLE) {
        setFailure(null);
        onUnregisteredRef.current();
        return;
      }
      setFailure({ title: title || UNREGISTER_FAILED_TITLE, message });
    };
    socket.on(GOT_TOURNAMENT_UNREGISTER, handleResult);
    socket.on(ERROR_TOURNAMENT_UNREGISTER, handleResult);
    return () => {
      socket.off(GOT_TOURNAMENT_UNREGISTER, handleResult);
      socket.off(ERROR_TOURNAMENT_UNREGISTER, handleResult);
    };
  }, [socket, tournamentId]);

  useEffect(() => clearTimer, []);

  // A reply that never comes: release the lock at the shared ceiling, counted
  // from when the request was sent (a reopened panel inherits the remainder).
  useEffect(() => {
    if (!pending) return undefined;
    const release = setTimeout(() => {
      if (!awaitingRef.current) return;
      awaitingRef.current = false;
      settleSitGoUnregister(tournamentId);
      clearTimer();
      setPending(false);
      setStillConfirming(false);
      setFailure({ title: UNREGISTER_UNCONFIRMED_TITLE, message: UNREGISTER_UNCONFIRMED_MESSAGE });
    }, sitGoUnregisterReleaseIn(tournamentId));
    return () => clearTimeout(release);
  }, [pending, tournamentId]);

  const requestUnregister = useCallback(() => {
    if (awaitingRef.current || isSitGoUnregisterInFlight(tournamentId)) return;
    // Same liveness question rejectUnsendableTableCommand asks: a transport
    // that cannot say (a test double) is treated as live.
    if (typeof socket?.canDeliver === 'function' && socket.canDeliver(GET_TOURNAMENT) === false) {
      setConnectionLost(true);
      return;
    }
    awaitingRef.current = true;
    markSitGoUnregisterSent(tournamentId);
    setConnectionLost(false);
    setPending(true);
    setStillConfirming(false);
    setFailure(null);
    unregister(tournamentId, gameID);
    clearTimer();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setStillConfirming(true);
    }, SIT_GO_UNREGISTER_TIMEOUT_MS);
  }, [socket, unregister, tournamentId, gameID]);

  return { requestUnregister, pending, stillConfirming, connectionLost, failure };
};
